import { useState, useCallback, useEffect, useRef } from 'react';
import { ApiUrls, fetchWithAuth } from '@ideoz/core';
// Single source of truth for size + allowed types (same as the upload
// dialogs) — see @ideoz/core/utils/attachment-types.
import {
  MAX_ATTACHMENT_SIZE_BYTES,
  ALLOWED_ATTACHMENT_MIME_TYPES,
  isAllowedAttachmentName,
} from '@ideoz/core/utils/attachment-types';

export interface UploadedAttachment {
  id: string;
  file: File;
  preview?: string;
  isImage: boolean;
  isUploading: boolean;
  uploadProgress?: number;
  error?: string;
}

export interface UseFileAttachmentsOptions {
  maxFiles?: number;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  maxTotalSize?: number;
  conversationId?: string | null;
  llmFamily?: string;
  /** Create or resolve a conversation shell when uploading before the first message. */
  onEnsureConversationId?: () => Promise<string | undefined>;
  onUploadComplete?: (attachmentId: string) => void;
  onUploadError?: (error: string) => void;
}

export interface UseFileAttachmentsReturn {
  attachments: UploadedAttachment[];
  isUploading: boolean;
  addFiles: (files: FileList | File[]) => Promise<void>;
  removeFile: (fileId: string) => Promise<void>;
  clearAttachments: () => void;
  getAttachmentIds: () => string[];
}

/**
 * Manages chat file attachments. Each selected file is uploaded immediately via
 * POST /attachments/chat-attachments with `conversation_id` (not on submit).
 */
export const useFileAttachments = ({
  maxFiles = 10,
  // Same per-file limit as every upload dialog (10MB). Total is capped at
  // maxFiles × per-file so the per-file limit is the real constraint.
  maxFileSize = MAX_ATTACHMENT_SIZE_BYTES,
  maxTotalSize = MAX_ATTACHMENT_SIZE_BYTES * 10,
  allowedFileTypes = [...ALLOWED_ATTACHMENT_MIME_TYPES],
  conversationId,
  llmFamily,
  onEnsureConversationId,
  onUploadComplete,
  onUploadError,
}: UseFileAttachmentsOptions): UseFileAttachmentsReturn => {
  const uploadEndpoint = `${ApiUrls.chatAttachments}`;
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [, setUploadProgress] = useState<{ [key: string]: number }>({});
  const conversationIdRef = useRef(conversationId ?? undefined);
  const onEnsureConversationIdRef = useRef(onEnsureConversationId);

  useEffect(() => {
    conversationIdRef.current = conversationId ?? undefined;
  }, [conversationId]);

  useEffect(() => {
    onEnsureConversationIdRef.current = onEnsureConversationId;
  }, [onEnsureConversationId]);

  const generateFileId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const getCurrentTotalSize = useCallback(() => {
    return attachments.reduce((total, att) => total + att.file.size, 0);
  }, [attachments]);

  // Accept when the MIME matches the allow-list OR the file extension is in
  // the shared allow-list. The extension check covers files the browser
  // reports with an empty/wrong MIME type (common for docx/xlsx/csv/md).
  const isFileTypeAllowed = useCallback(
    (file: File) =>
      allowedFileTypes.includes(file.type) || isAllowedAttachmentName(file.name),
    [allowedFileTypes]
  );

  const isFileSizeAllowed = useCallback(
    (file: File) => file.size <= maxFileSize,
    [maxFileSize]
  );

  const isFileNotEmpty = useCallback((file: File) => file.size > 0, []);

  const isImageFile = (file: File) => file.type.startsWith('image/');

  const createFilePreview = async (file: File): Promise<string | undefined> => {
    if (isImageFile(file)) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      });
    }
    return undefined;
  };

  const resolveConversationId = async (): Promise<string | undefined> => {
    if (conversationIdRef.current) {
      return conversationIdRef.current;
    }
    const ensure = onEnsureConversationIdRef.current;
    if (!ensure) {
      return undefined;
    }
    const id = await ensure();
    if (id) {
      conversationIdRef.current = id;
    }
    return id;
  };

  const uploadFileToServer = async (
    file: File,
    tempId: string,
    targetConversationId: string
  ): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('conversation_id', targetConversationId);
    if (llmFamily) {
      formData.append('llm_family', llmFamily);
    }

    const simulateProgress = () => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 15;
        if (progress >= 90) {
          progress = 90;
          clearInterval(interval);
        }
        setUploadProgress((prev) => ({ ...prev, [tempId]: Math.round(progress) }));
        setAttachments((prev) =>
          prev.map((att) =>
            att.id === tempId ? { ...att, uploadProgress: Math.round(progress) } : att
          )
        );
      }, 100);
      return interval;
    };

    const progressInterval = simulateProgress();

    try {
      const response = await fetchWithAuth(uploadEndpoint, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData?.message || errorData?.error || errorMessage;
        } catch {
          /* use default */
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      const attachmentId = responseData.id || responseData.attachmentId;
      if (!attachmentId) {
        throw new Error('No attachment ID received from server');
      }

      setAttachments((prev) =>
        prev.map((att) =>
          att.id === tempId
            ? { ...att, id: attachmentId, isUploading: false, uploadProgress: 100 }
            : att
        )
      );

      setUploadProgress((prev) => {
        const next = { ...prev };
        delete next[tempId];
        return next;
      });

      onUploadComplete?.(attachmentId);
      return attachmentId;
    } catch (error) {
      clearInterval(progressInterval);
      setAttachments((prev) =>
        prev.map((att) =>
          att.id === tempId
            ? {
                ...att,
                isUploading: false,
                error: error instanceof Error ? error.message : 'Upload failed',
              }
            : att
        )
      );
      throw error instanceof Error ? error : new Error('Upload failed');
    }
  };

  const deleteFile = async (attachmentId: string): Promise<void> => {
    const response = await fetchWithAuth(`${uploadEndpoint}${attachmentId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
    }
  };

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const emptyFiles = fileArray.filter((file) => !isFileNotEmpty(file));
      const oversizedFiles = fileArray.filter(
        (file) => isFileNotEmpty(file) && !isFileSizeAllowed(file)
      );
      const validFiles = fileArray.filter(
        (file) => isFileNotEmpty(file) && isFileTypeAllowed(file) && isFileSizeAllowed(file)
      );
      const invalidTypeFiles = fileArray.filter(
        (file) => isFileNotEmpty(file) && !isFileTypeAllowed(file) && isFileSizeAllowed(file)
      );

      const currentTotalSize = getCurrentTotalSize();
      const maxTotalSizeKB = Math.round(maxTotalSize / 1024);
      let totalSizeWithNewFiles = currentTotalSize;
      const acceptedFiles: File[] = [];

      for (const file of validFiles) {
        if (totalSizeWithNewFiles + file.size <= maxTotalSize) {
          acceptedFiles.push(file);
          totalSizeWithNewFiles += file.size;
        }
      }

      if (emptyFiles.length > 0) {
        onUploadError?.(`Empty files cannot be uploaded: ${emptyFiles.map((f) => f.name).join(', ')}`);
      }
      if (oversizedFiles.length > 0) {
        const maxSizeLabel =
          maxFileSize >= 1024 * 1024
            ? `${Math.round(maxFileSize / (1024 * 1024))}MB`
            : `${Math.round(maxFileSize / 1024)}KB`;
        onUploadError?.(
          `Files exceeding ${maxSizeLabel} cannot be uploaded: ${oversizedFiles.map((f) => f.name).join(', ')}`
        );
      }
      if (invalidTypeFiles.length > 0) {
        onUploadError?.(
          `Unsupported file types: ${invalidTypeFiles.map((f) => f.name).join(', ')}`
        );
      }
      if (validFiles.length > acceptedFiles.length) {
        onUploadError?.(`Total size limit of ${maxTotalSizeKB}KB would be exceeded`);
      }
      if (acceptedFiles.length === 0) return;

      if (attachments.length + acceptedFiles.length > maxFiles) {
        onUploadError?.(`Maximum ${maxFiles} files allowed`);
        acceptedFiles.splice(maxFiles - attachments.length);
        if (acceptedFiles.length === 0) return;
      }

      const targetConversationId = await resolveConversationId();
      if (!targetConversationId) {
        onUploadError?.(
          'A conversation is required before uploading files. Send a message first or open an existing chat.'
        );
        return;
      }

      const newAttachments: UploadedAttachment[] = [];
      for (const file of acceptedFiles) {
        const tempId = generateFileId();
        const preview = await createFilePreview(file);
        newAttachments.push({
          id: tempId,
          file,
          preview,
          isImage: isImageFile(file),
          isUploading: true,
          uploadProgress: 0,
        });
      }

      setAttachments((prev) => [...prev, ...newAttachments]);

      for (const attachment of newAttachments) {
        try {
          await uploadFileToServer(attachment.file, attachment.id, targetConversationId);
        } catch (error) {
          onUploadError?.(
            `Failed to upload "${attachment.file.name}": ${error instanceof Error ? error.message : 'Upload failed'}`
          );
        }
      }
    },
    [
      attachments.length,
      maxFiles,
      maxFileSize,
      maxTotalSize,
      isFileTypeAllowed,
      isFileSizeAllowed,
      isFileNotEmpty,
      getCurrentTotalSize,
      llmFamily,
      onUploadError,
    ]
  );

  const removeFile = useCallback(
    async (fileId: string) => {
      const attachment = attachments.find((att) => att.id === fileId);
      if (!attachment) return;

      if (attachment.isUploading) {
        setAttachments((prev) => prev.filter((att) => att.id !== fileId));
        return;
      }

      try {
        await deleteFile(fileId);
      } catch {
        /* still remove from UI */
      }
      setAttachments((prev) => prev.filter((att) => att.id !== fileId));
    },
    [attachments]
  );

  const clearAttachments = useCallback(() => {
    setAttachments([]);
    setUploadProgress({});
  }, []);

  const isUploading = attachments.some((att) => att.isUploading);

  const getAttachmentIds = useCallback(() => {
    return attachments.filter((att) => !att.isUploading && !att.error).map((att) => att.id);
  }, [attachments]);

  return {
    attachments,
    isUploading,
    addFiles,
    removeFile,
    clearAttachments,
    getAttachmentIds,
  };
};
